<template>
  <aside class="city-sidebar">
    <div class="city-sidebar-head">
      <i class="fa-solid fa-city"></i>
      <span>{{ $t('regionBar.cityNavigation') }}</span>
    </div>

    <div class="city-sidebar-search">
      <i class="fa-solid fa-magnifying-glass"></i>
      <input v-model="query" type="search" :placeholder="$t('regionBar.citySearchPlaceholder')" />
    </div>

    <ul class="city-sidebar-list">
      <li v-for="city in filteredCities" :key="city.slug">
        <NuxtLink
          :to="localePath(`/region/bar/${city.slug}`)"
          class="city-sidebar-item"
          :class="{ active: city.slug === current }"
        >
          <span class="city-sidebar-item-label">{{ city.label }}</span>
          <i v-if="city.slug === current" class="fa-solid fa-check city-sidebar-item-check"></i>
        </NuxtLink>
      </li>
      <li v-if="filteredCities.length === 0" class="city-sidebar-empty">
        {{ $t('regionBar.cityNoResults') }}
      </li>
    </ul>
  </aside>
</template>

<script setup lang="ts">
/**
 * RegionBar CitySidebar
 *
 * A plain list of city links used to switch between price-list pages.
 * Uses <NuxtLink> directly (no combobox / value resolution) so the labels
 * always render correctly and navigation is instant.
 */
interface CityOption {
  slug: string
  label: string
}

const props = defineProps<{
  cities: CityOption[],
  current: string
}>()

const query = ref('')

const localePath = useLocalePath()

const filteredCities = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return props.cities
  return props.cities.filter((c) => c.label.toLowerCase().includes(q))
})
</script>

<style scoped lang="scss">
.city-sidebar {
  background: #fff;
  border: 1px solid #eef1f4;
  border-radius: 12px;
  overflow: hidden;
  position: sticky;
  top: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);

  .city-sidebar-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 16px;
    background: #f8f9fa;
    color: #334155;
    font-size: 0.95rem;
    font-weight: 700;

    i {
      color: #03a054;
    }
  }

  .city-sidebar-search {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    margin: 12px 12px 0;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #fafbfc;

    i {
      color: #94a3b8;
      font-size: 0.85rem;
    }

    input {
      flex: 1;
      min-width: 0;
      border: none;
      background: transparent;
      outline: none;
      font-size: 0.85rem;
      color: #334155;
      font-family: inherit;

      &::placeholder {
        color: #94a3b8;
      }
    }
  }

  .city-sidebar-list {
    list-style: none;
    margin: 0;
    padding: 8px 12px 14px;
    max-height: 880px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .city-sidebar-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 9px 12px;
      border-radius: 8px;
      color: #475569;
      font-size: 0.88rem;
      font-weight: 500;
      text-decoration: none;
      cursor: pointer;
      transition: background 0.2s ease, color 0.2s ease;

      &:hover {
        background: #f0fdf4;
        color: #03a054;
      }

      &.active {
        background: #03a054;
        color: #fff;
      }

      .city-sidebar-item-check {
        font-size: 0.8rem;
      }
    }

    .city-sidebar-empty {
      padding: 12px;
      text-align: center;
      color: #94a3b8;
      font-size: 0.85rem;
    }
  }
}
</style>
